
import { isChrome, isWindows } from '../../compat.js';
import { trackWebGPUDeviceCreated } from '../../analytics/index.js';

import { RequiredBytesPerSample } from './CommonRenderTargets.js';
import { requestWebGPUAdapter, validateWebGPUFeaturesAndLimits, expectWebGPUCompatibilitySupport } from './Support.js';

// Shared WebGPU device used by default for viewer initializations if WebGPU is enabled and available
let _defaultDevice = null;
let _nestedInits = 0; // Allow nesting of GetOrCreateWebGPUDevice calls

let _gpuAdapterFeatures = null;
export function GetGpuAdapterFeatures() {
  return _gpuAdapterFeatures;
}

let _gpuAdapterInfo = null;
export function GetGpuAdapterInfo() {
  return _gpuAdapterInfo;
}

let _gpuAdapterLimits = null;
export function GetGpuAdapterLimits() {
  return _gpuAdapterLimits;
}

let _usingMultiDraw = false;
export function UsingMultiDraw() {
  return _usingMultiDraw;
}

let _usingDrawIndirect = false;
export function UsingDrawIndirect() {
  return _usingDrawIndirect;
}

let _usingTimestampQuery = false;
export function UsingTimestampQuery() {
  return _usingTimestampQuery;
}

// Track if running in compatibility mode
let _isCompatibilityMode = false;
export function IsCompatibilityMode() {
  return _isCompatibilityMode;
}

// Track if running with degraded features (some optimal limits not met)
let _isDegraded = false;
export function IsDegraded() {
  return _isDegraded;
}

// https://developer.mozilla.org/en-US/docs/Web/API/GPU/requestAdapter#options
/** @type {GPURequestAdapterOptions} */
const DefaultAdapterOptions = Object.freeze({
  powerPreference: 'high-performance'
  // featureLevel: 'compatibility', // causes 'core-features-and-limits' to be requested
});

// https://developer.mozilla.org/en-US/docs/Web/API/GPUSupportedFeatures
/** @type {GPUSupportedFeatures} */
const DefaultDeviceFeatures = Object.freeze(new Set([
  // 'chromium-experimental-multi-draw-indirect',
  // 'indirect-first-instance',
  // 'timestamp-query',
  // 'texture-compression-bc'
]));

// These are preferred and required limits for our WebGPU devices. We may want to make these configurable later to
// account for rendering passes from extensions or other use cases. These limits should include requests required
// explicitly for compatibility mode (core-features-and-limits).

/** @type {GPUSupportedLimits} */
const PreferredDeviceLimits = Object.freeze({
  maxColorAttachmentBytesPerSample: RequiredBytesPerSample
});

/** @type {GPUSupportedLimits} */
const RequiredDeviceLimits = Object.freeze({
  maxColorAttachmentBytesPerSample: 32
});

/**
 * Creates a WebGPU device with automatic fallback to compatibility mode.
 *
 * @param {GPURequestAdapterOptions} adapterOptions - Passed to navigator.gpu.requestAdapter
 * @param {GPUSupportedFeatures} deviceFeatures - Passed to adapter.requestDevice
 * @returns {Promise<GPUDevice|null>}
 */
export async function CreateWebGPUDevice(adapterOptions, deviceFeatures) {

  _isDegraded = _isCompatibilityMode = false;
  _gpuAdapterFeatures = _gpuAdapterInfo = _gpuAdapterLimits = null;

  // PREPARE ADAPTER OPTIONS FOR REQUEST

  // Merge default and provided options and features
  const mergedAdapterOptions = Object.assign({}, DefaultAdapterOptions, adapterOptions);
  const requiredDeviceFeatures = new Set([...DefaultDeviceFeatures, ...(deviceFeatures || [])]);

  // Remove powerPreference for Chrome on Windows to avoid console warnings
  if (isWindows() && isChrome() && mergedAdapterOptions.powerPreference) {
    delete mergedAdapterOptions.powerPreference;
  }

  // Check if compatibility mode is explicitly requested
  const explicitCompatibility = mergedAdapterOptions.featureLevel === 'compatibility';
  if (explicitCompatibility) {
    console.warn('WebGPU compatibility mode explicitly requested.');
  }

  // REQUEST ADAPTER

  // First attempt with current options (core by default, or compatibility if explicitly requested)
  let adapter = await requestWebGPUAdapter(mergedAdapterOptions);

  // Fallback to compatibility mode if core adapter failed and compatibility is expected to be supported
  if (adapter === null && !explicitCompatibility && expectWebGPUCompatibilitySupport()) {
    console.info('No core WebGPU adapter available, trying compatibility mode...');
    mergedAdapterOptions.featureLevel = 'compatibility';
    adapter = await requestWebGPUAdapter(mergedAdapterOptions);
  }

  if (adapter === null) {
    console.warn('No WebGPU adapter available.');
    return null;
  }

  // Check if we're in compatibility mode (either explicitly requested or via automatic fallback).
  // Unlike other WebGPU features, we don't explicitly request 'core-features-and-limits' on the device -
  // compatibility mode is inherited from the adapter's featureLevel. This may change once compatibility
  // mode graduates from experimental.
  const compatibilityRequested = mergedAdapterOptions.featureLevel === 'compatibility';
  const adapterSupportsCompatibility = adapter.features.has('core-features-and-limits');
  _isCompatibilityMode = compatibilityRequested && adapterSupportsCompatibility;
  if (_isCompatibilityMode) {
    console.info('WebGPU adapter acquired in compatibility mode.');
  } else if (compatibilityRequested && !adapterSupportsCompatibility) {
    console.warn('WebGPU compatibility mode was requested but the adapter does not support it.');
  }

  // VALIDATE FEATURES AND LIMITS

  const { valid, degraded } = validateWebGPUFeaturesAndLimits(adapter, requiredDeviceFeatures, PreferredDeviceLimits, RequiredDeviceLimits);
  if (!valid) {
    console.warn('WebGPU adapter does not meet required features or limits.');
    return null; // @todo: Track this as a viewer.webgpu.device.incompatible analytics event
  }
  _isDegraded = Object.keys(degraded).length > 0;
  _isDegraded && console.warn('WebGPU adapter does not meet preferred limits, running in degraded mode.');

  // CREATE DEVICE

  // Save adapter features, info, and limits for analytics on context loss (and creation)
  _gpuAdapterFeatures = adapter.features;
  _gpuAdapterInfo = adapter.info;
  _gpuAdapterLimits = adapter.limits;

  // Build device limits: start with required, override with preferred, then apply degraded
  const requiredDeviceLimits = Object.assign({}, RequiredDeviceLimits, PreferredDeviceLimits, degraded);

  // Request the adapter's full maxTextureDimension2D so that high-res offscreen renders
  // (e.g. print screenshots) can use the GPU's actual capability instead of the WebGPU
  // default of 8192. Since we request exactly what the adapter reports, this cannot fail.
  const adapterMaxTexDim = adapter.limits.maxTextureDimension2D;
  if (adapterMaxTexDim) {
    requiredDeviceLimits.maxTextureDimension2D = adapterMaxTexDim;
  }

  /** @type {GPUDeviceDescriptor} */
  const deviceDescriptor = { requiredFeatures: requiredDeviceFeatures, requiredLimits: requiredDeviceLimits };
  // Note: Adapters are single-use - requestDevice() can only be called once per adapter. We request a fresh
  // adapter each time CreateWebGPUDevice is called, so this is not an issue. requestDevice rejects with
  // OperationError (limits) or TypeError (features) if requirements exceed adapter capabilities. Since we
  // validated against adapter.limits/features above, rejection should not occur.
  const device = await adapter.requestDevice(deviceDescriptor);

  // requestDevice always resolves with a device, but it may already be in a lost state due to resource pressure,
  // driver issues, etc. See: https://toji.dev/webgpu-best-practices/device-loss.html
  const lostInfo = await Promise.race([device.lost, Promise.resolve(null)]);
  if (lostInfo) {
    console.warn(`WebGPU device was lost immediately after creation: ${lostInfo.reason} - ${lostInfo.message}`);
    return null;
  }

  // Keep track of available, successfully requested features
  _usingMultiDraw = device.features.has('chromium-experimental-multi-draw-indirect');
  _usingDrawIndirect = device.features.has('indirect-first-instance') && !_usingMultiDraw;
  _usingTimestampQuery = device.features.has('timestamp-query');

  console.groupCollapsed('WebGPU device created');
  console.log(_gpuAdapterInfo);
  console.log(device.limits);
  console.groupEnd();

  return device;
}

/**
 * Creates a shared WebGPU device for viewer initializations.
 *
 * @param {GPURequestAdapterOptions} adapterOptions - Passed to navigator.gpu.requestAdapter
 * @param {GPUSupportedFeatures} deviceFeatures - Passed to adapter.requestDevice
 * @param {boolean} [forceCompatibility=false]
 * @returns {Promise<GPUDevice|null>}
 */
export async function GetOrCreateWebGPUDevice(adapterOptions = new Object(), deviceFeatures = new Set(), forceCompatibility = false) {
  _nestedInits++;
  console.assert(_nestedInits > 0, 'Mismatched GetOrCreateWebGPUDevice / DestroyWebGPUDevice calls.');

  if (_defaultDevice) return _defaultDevice;

  if (forceCompatibility && !Object.hasOwn(adapterOptions, 'featureLevel')) {
    adapterOptions = Object.assign({}, adapterOptions, { featureLevel: 'compatibility' });
  }

  _defaultDevice = await CreateWebGPUDevice(adapterOptions, deviceFeatures);
  if (_defaultDevice) {
    trackWebGPUDeviceCreated(true);
  }
  return _defaultDevice;
}
/**
 * @deprecated Use GetOrCreateWebGPUDevice instead.
 */
export { GetOrCreateWebGPUDevice as InitWebGPU };

export function DestroyWebGPUDevice() {
  _nestedInits--;
  console.assert(_nestedInits >= 0, 'Mismatched GetOrCreateWebGPUDevice / DestroyWebGPUDevice calls.');

  if (_nestedInits === 0) {
    _defaultDevice.destroy();
    _defaultDevice = null;
  }
}
/**
 * @deprecated Use GetOrCreateWebGPUDevice instead.
 */
export { DestroyWebGPUDevice as ShutDownWebGPU }; // @todo: remove deprecated 'ShutDownWebGPU' on next major version

export function GetDefaultWebGPUDevice() {
  return _defaultDevice;
}